import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useFormatEUR } from "@/lib/useFormatEUR";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SEVERITY = { subtotaal_wijkt_af: "high", totaal_wijkt_af: "high", btw_bij_verlegd: "high", geen_regels: "high", te_veel_betaald: "high", btw_nummer_ontbreekt: "mid", geen_btw_standaard: "mid", yuki_mislukt: "mid", geen_voertuig: "low", yuki_niet_verstuurd: "low" };
const COLOR = { high: "bg-red-500/10 text-red-700 border-red-500/30", mid: "bg-amber-500/10 text-amber-700 border-amber-500/30", low: "bg-slate-500/10 text-slate-600 border-slate-500/30" };

export const InvoiceAuditDialog = ({ open, onOpenChange, onPick }) => {
  const { t } = useLang();
  const fm = useFormatEUR();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setData(null); setErr("");
    api.get("/invoices/audit").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="invoice-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" />{t("invoiceAudit")}</DialogTitle>
          <DialogDescription>{t("invoiceAuditSub")}</DialogDescription>
        </DialogHeader>
        {!data && !err && <div className="flex items-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin" />{t("checking")}</div>}
        {err && <p className="text-sm text-red-600" data-testid="invoice-audit-error">{err}</p>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-border p-3"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("total")}</div><div className="font-display text-2xl font-black" data-testid="audit-total">{data.total}</div></div>
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3"><div className="text-[11px] uppercase tracking-wider text-emerald-700">{t("inOrder")}</div><div className="font-display text-2xl font-black text-emerald-700" data-testid="audit-ok">{data.ok}</div></div>
              <div className={`rounded-xl border p-3 ${data.with_issues ? "border-amber-500/30 bg-amber-500/5" : "border-border"}`}><div className="text-[11px] uppercase tracking-wider text-amber-700">{t("needsAttention")}</div><div className="font-display text-2xl font-black text-amber-700" data-testid="audit-issues">{data.with_issues}</div></div>
            </div>
            {data.with_issues === 0 && <div className="flex items-center gap-2 text-emerald-700 text-sm py-2" data-testid="audit-all-ok"><CheckCircle2 className="h-5 w-5" />{t("allInvoicesOk")}</div>}
            {Object.keys(data.counts).length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="audit-counts">
                {Object.entries(data.counts).sort((a, b) => b[1] - a[1]).map(([c, n]) => <Badge key={c} variant="outline" className={`rounded-full ${COLOR[SEVERITY[c] || "low"]}`}>{t(`audit_${c}`)} · {n}</Badge>)}
              </div>
            )}
            <div className="space-y-2">
              {data.issues.map((i) => (
                <button key={i.id} type="button" onClick={() => { onPick?.(i.invoice_number); onOpenChange(false); }} className="w-full text-left rounded-xl border border-border hover:border-primary/50 p-3 transition-colors" data-testid={`audit-row-${i.invoice_number}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0"><span className="font-mono font-bold">{i.invoice_number}</span> <span className="text-muted-foreground truncate">· {i.customer_name}</span></div>
                    <div className="flex items-center gap-2 shrink-0"><span className="font-mono text-sm">{fm(i.total)}</span>{i.vat_regime !== "standard" && <Badge variant="outline" className="rounded-full text-[10px]">{i.vat_regime === "reverse_charge" ? "BTW verlegd" : "Vrijgesteld"}</Badge>}</div>
                  </div>
                  <ul className="mt-1.5 space-y-0.5">
                    {i.problems.map((p, k) => <li key={k} className={`text-xs flex items-start gap-1.5 ${SEVERITY[p.code] === "high" ? "text-red-700" : SEVERITY[p.code] === "mid" ? "text-amber-700" : "text-muted-foreground"}`}><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{p.text}</li>)}
                  </ul>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
